<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Date Picker Component
      p.text--secondary A customizable date picker component for single date and date range selection.

    // Date Picker with yz-menu
    .component-section.mb-8#with-menu
      h3.mb-4.cursor-pointer(@click="navigateToSection('with-menu')") With YZ Menu

      .variation-item.mb-6#menu-single-date
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('menu-single-date')") Single Date Selection
          p.text--secondary.caption Date picker opened in a menu attached to a text field

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.flex-column(style="max-width: 400px;")
            label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date
            yz-menu(
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            )
              template(v-slot:activator="{ on, attrs }")
                yz-text-field(
                  v-model="formattedSingleDate"
                  placeholder="Pick a date"
                  prepend-inner-icon="$yzi-calendar"
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  readonly
                  clickable
                  @click:clear="singleDate = null"
                )
              yz-date-picker(
                v-model="singleDate"
                @input="menuSingle = false"
                scrollable
              )
            .mt-2.text-caption.text--secondary
              strong Selected date:
              code.pa-1.rounded(style="background-color: #f0f0f0;") {{ singleDate || 'null' }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === singleDateMenuCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === singleDateMenuCode ? 'success' : ''" @click="copyCode(singleDateMenuCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ singleDateMenuCode }}

      .variation-item.mb-6#menu-date-range
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('menu-date-range')") Date Range Selection
          p.text--secondary.caption Date range picker opened in a menu

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.flex-column(style="max-width: 400px;")
            label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date Range
            yz-menu(
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            )
              template(v-slot:activator="{ on, attrs }")
                yz-text-field(
                  v-model="formattedDateRange"
                  placeholder="Pick date range"
                  prepend-inner-icon="$yzi-calendar"
                  readonly
                  clickable
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="dateRange = []"
                )
              yz-date-picker(
                v-model="dateRange"
                range
                scrollable
              )
            .mt-2.text-caption.text--secondary
              strong Selected range:
              code.pa-1.rounded(style="background-color: #f0f0f0;") {{ dateRange.length > 0 ? dateRange.join(' to ') : 'null' }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === dateRangeMenuCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === dateRangeMenuCode ? 'success' : ''" @click="copyCode(dateRangeMenuCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ dateRangeMenuCode }}

    // Date Picker with yz-dialog
    .component-section.mb-8#with-dialog
      h3.mb-4.cursor-pointer(@click="navigateToSection('with-dialog')") With YZ Dialog

      .variation-item.mb-6#dialog-single-date
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('dialog-single-date')") Single Date Selection
          p.text--secondary.caption Date picker opened in a dialog modal

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.flex-column(style="max-width: 400px;")
            label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date

            .mt-2.text-caption.text--secondary
              strong Selected date:
              code.pa-1.rounded(style="background-color: #f0f0f0;") {{ dialogSingleDate || 'null' }}

            yz-text-field(
              v-model="formattedDialogSingleDate"
              placeholder="Pick a date"
              prepend-inner-icon="$yzi-calendar"
              readonly
              clickable
              clearable
              required
              @click="dialogSingle = true"
              @click:clear="dialogSingleDate = null"
            )
          yz-dialog(v-model="dialogSingle" max-width="380px")
            yz-card
              yz-dialog-normal-content.px-2.pt-2.pb-0
                yz-date-picker(
                  v-model="dialogSingleDate"
                  scrollable,
                  @change="dialogSingle = false"
                )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === singleDateDialogCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === singleDateDialogCode ? 'success' : ''" @click="copyCode(singleDateDialogCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ singleDateDialogCode }}

      .variation-item.mb-6#dialog-date-range
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('dialog-date-range')") Date Range Selection
          p.text--secondary.caption Date range picker opened in a dialog modal

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.flex-column(style="max-width: 400px;")
            label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date Range
            yz-text-field(
              v-model="formattedDialogDateRange"
              placeholder="Pick date range"
              prepend-inner-icon="$yzi-calendar"
              readonly
              clickable
              @click="dialogRange = true"
              clearable
              required
              @click:clear="dialogDateRange = []"
            )
            .mt-2.text-caption.text--secondary
              strong Selected range:
              code.pa-1.rounded(style="background-color: #f0f0f0;") {{ dialogDateRange.length > 0 ? dialogDateRange.join(' to ') : 'null' }}

          yz-dialog(v-model="dialogRange" max-width="380px")
            yz-card
              yz-dialog-normal-content.px-2.pt-2.pb-0
                yz-date-picker(
                  v-model="dialogDateRange"
                  range
                  scrollable,
                  @change="dialogRange = false"
                )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === dateRangeDialogCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === dateRangeDialogCode ? 'success' : ''" @click="copyCode(dateRangeDialogCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ dateRangeDialogCode }}

    // Advanced Examples
    .component-section.mb-8#advanced
      h3.mb-4.cursor-pointer(@click="navigateToSection('advanced')") Advanced Examples

      .variation-item.mb-6#min-max-dates
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('min-max-dates')") Min/Max Date Restrictions
          p.text--secondary.caption Date picker with date range restrictions

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.flex-column(style="max-width: 400px;")
            label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date (Last 30 Days Only)
            yz-menu(
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            )
              template(v-slot:activator="{ on, attrs }")
                yz-text-field(
                  v-model="formattedRestrictedDate"
                  placeholder="Pick a date"
                  prepend-inner-icon="$yzi-calendar"
                  readonly
                  clickable
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="restrictedDate = null"
                )
              yz-date-picker(
                v-model="restrictedDate"
                @input="menuRestricted = false"
                :min="minDate"
                :max="maxDate"
                scrollable
              )
            .mt-2.text-caption.text--secondary
              strong Selected date:
              code.pa-1.rounded(style="background-color: #f0f0f0;") {{ restrictedDate || 'null' }}
            .mt-1.text-caption.text--secondary
              span Allowed: {{ minDate }} to {{ maxDate }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === minMaxDateCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === minMaxDateCode ? 'success' : ''" @click="copyCode(minMaxDateCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ minMaxDateCode }}

      .variation-item.mb-6#allowed-dates
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('allowed-dates')") Allowed Dates Function
          p.text--secondary.caption Only allow specific dates (e.g., weekdays only)

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.flex-column(style="max-width: 400px;")
            label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date (Weekdays Only)
            yz-menu(
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            )
              template(v-slot:activator="{ on, attrs }")
                yz-text-field(
                  v-model="formattedWeekdayDate"
                  placeholder="Pick a weekday"
                  prepend-inner-icon="$yzi-calendar"
                  readonly
                  clickable
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="weekdayDate = null"
                )
              yz-date-picker(
                v-model="weekdayDate"
                @input="menuWeekday = false"
                :allowed-dates="allowedWeekdays"
                scrollable
              )
            .mt-2.text-caption.text--secondary
              strong Selected date:
              code.pa-1.rounded(style="background-color: #f0f0f0;") {{ weekdayDate || 'null' }}
            .mt-1.text-caption.text--secondary
              span Only weekdays (Monday-Friday) are selectable

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === allowedDatesCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === allowedDatesCode ? 'success' : ''" @click="copyCode(allowedDatesCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ allowedDatesCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in datePickerProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in datePickerEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}
</template>

<script>
import { DateTime } from 'luxon'

export default {
  data() {
    return {
      copiedCode: null,

      // Menu single date
      singleDate: null,
      menuSingle: false,

      // Menu date range
      dateRange: [],
      menuRange: false,

      // Dialog single date
      dialogSingleDate: null,
      dialogSingle: false,

      // Dialog date range
      dialogDateRange: [],
      dialogRange: false,

      // Restricted dates
      restrictedDate: null,
      menuRestricted: false,

      // Weekday only
      weekdayDate: null,
      menuWeekday: false,

      // Code examples
      singleDateMenuCode: `// Template
label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date
yz-menu(
  :close-on-content-click="false"
  :nudge-right="40"
  transition="scale-transition"
  offset-y
  min-width="auto"
)
  template(v-slot:activator="{ on, attrs }")
    yz-text-field(
      v-model="formattedDate"
      placeholder="Pick a date"
      prepend-inner-icon="$yzi-calendar"
      readonly
      clickable
      v-bind="attrs"
      v-on="on"
      clearable
      @click:clear="date = null"
    )
  yz-date-picker(
    v-model="date"
    @input="menu = false"
    scrollable
  )

// Script
import { DateTime } from 'luxon'

data() {
  return {
    date: null,
    menu: false
  }
},
computed: {
  formattedDate() {
    if (!this.date) return ''
    return DateTime.fromISO(this.date).toFormat('dd/MM/yyyy')
  }
}`,

      dateRangeMenuCode: `// Template
label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date Range
yz-menu(
  :close-on-content-click="false"
  :nudge-right="40"
  transition="scale-transition"
  offset-y
  min-width="auto"
)
  template(v-slot:activator="{ on, attrs }")
    yz-text-field(
      v-model="formattedDateRange"
      placeholder="Pick date range"
      prepend-inner-icon="$yzi-calendar"
      readonly
      clickable
      v-bind="attrs"
      v-on="on"
      clearable
      @click:clear="dateRange = []"
    )
  yz-date-picker(
    v-model="dateRange"
    range
    scrollable
  )

// Script
import { DateTime } from 'luxon'

data() {
  return {
    dateRange: []
  }
},
computed: {
  formattedDateRange() {
    if (this.dateRange.length === 0) return ''
    if (this.dateRange.length === 1) {
      return DateTime.fromISO(this.dateRange[0]).toFormat('dd/MM/yyyy')
    }
    const dates = [...this.dateRange].sort()
    return \`\${DateTime.fromISO(dates[0]).toFormat('dd/MM/yyyy')} - \${DateTime.fromISO(dates[1]).toFormat('dd/MM/yyyy')}\`
  }
}`,

      singleDateDialogCode: `// Template
label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date

.mt-2.text-caption.text--secondary
  strong Selected date:
  code.pa-1.rounded(style="background-color: #f0f0f0;") {{ date || 'null' }}

yz-text-field(
  v-model="formattedDate"
  placeholder="Pick a date"
  prepend-inner-icon="$yzi-calendar"
  readonly
  clickable
  clearable
  required
  @click="dialog = true"
  @click:clear="date = null"
)

yz-dialog(v-model="dialog" max-width="380px")
  yz-card
    yz-dialog-normal-content.px-2.pt-2.pb-0
      yz-date-picker(
        v-model="date"
        scrollable,
        @change="dialog = false"
      )

// Script
import { DateTime } from 'luxon'

data() {
  return {
    date: null,
    dialog: false
  }
},
computed: {
  formattedDate() {
    if (!this.date) return ''
    return DateTime.fromISO(this.date).toFormat('dd/MM/yyyy')
  }
}`,

      dateRangeDialogCode: `// Template
label.yz-regular-body-sm.font-weight-medium.mb-2 Select Date Range
yz-text-field(
  v-model="formattedDateRange"
  placeholder="Pick date range"
  prepend-inner-icon="$yzi-calendar"
  readonly
  clickable
  @click="dialog = true"
  clearable
  required
  @click:clear="dateRange = []"
)
.mt-2.text-caption.text--secondary
  strong Selected range:
  code.pa-1.rounded(style="background-color: #f0f0f0;") {{ dateRange.length > 0 ? dateRange.join(' to ') : 'null' }}

yz-dialog(v-model="dialog" max-width="380px")
  yz-card
    yz-dialog-normal-content.px-2.pt-2.pb-0
      yz-date-picker(
        v-model="dateRange"
        range
        scrollable,
        @change="dialog = false"
      )

// Script
import { DateTime } from 'luxon'

data() {
  return {
    dateRange: [],
    dialog: false
  }
},
computed: {
  formattedDateRange() {
    if (this.dateRange.length === 0) return ''
    if (this.dateRange.length === 1) {
      return DateTime.fromISO(this.dateRange[0]).toFormat('dd/MM/yyyy')
    }
    const dates = [...this.dateRange].sort()
    return \`\${DateTime.fromISO(dates[0]).toFormat('dd/MM/yyyy')} - \${DateTime.fromISO(dates[1]).toFormat('dd/MM/yyyy')}\`
  }
}`,

      minMaxDateCode: `// Template
yz-date-picker(
  v-model="date"
  :min="minDate"
  :max="maxDate"
  scrollable
)

// Script
data() {
  return {
    date: null
  }
},
computed: {
  minDate() {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().substring(0, 10)
  },
  maxDate() {
    return new Date().toISOString().substring(0, 10)
  }
}`,

      allowedDatesCode: `// Template
yz-date-picker(
  v-model="date"
  :allowed-dates="allowedWeekdays"
  scrollable
)

// Script
data() {
  return {
    date: null
  }
},
methods: {
  allowedWeekdays(val) {
    const day = new Date(val).getDay()
    return day !== 0 && day !== 6 // Exclude Sunday (0) and Saturday (6)
  }
}`
    }
  },
  computed: {
    // Format single date for display
    formattedSingleDate() {
      if (!this.singleDate) return ''
      return this.singleDate
      // return this.formatDate(this.singleDate)
    },

    // Format date range for display
    formattedDateRange() {
      if (this.dateRange.length === 0) return ''
      if (this.dateRange.length === 1) {
        return this.formatDate(this.dateRange[0])
      }
      const dates = [...this.dateRange].sort()
      return `${this.formatDate(dates[0])} - ${this.formatDate(dates[1])}`
    },

    // Format dialog single date for display
    formattedDialogSingleDate() {
      if (!this.dialogSingleDate) return ''
      return this.formatDate(this.dialogSingleDate)
    },

    // Format dialog date range for display
    formattedDialogDateRange() {
      if (this.dialogDateRange.length === 0) return ''
      if (this.dialogDateRange.length === 1) {
        return this.formatDate(this.dialogDateRange[0])
      }
      const dates = [...this.dialogDateRange].sort()
      return `${this.formatDate(dates[0])} - ${this.formatDate(dates[1])}`
    },

    // Format restricted date for display
    formattedRestrictedDate() {
      if (!this.restrictedDate) return ''
      return this.formatDate(this.restrictedDate)
    },

    // Format weekday date for display
    formattedWeekdayDate() {
      if (!this.weekdayDate) return ''
      return this.formatDate(this.weekdayDate)
    },

    // Min date (30 days ago)
    minDate() {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().substring(0, 10)
    },

    // Max date (today)
    maxDate() {
      return new Date().toISOString().substring(0, 10)
    },

    datePickerProps() {
      return [
        {
          name: 'value',
          type: 'String | Array',
          default: 'null',
          description: 'The selected date (v-model). String for single date, Array for date range'
        },
        {
          name: 'pickerDate',
          type: 'String',
          default: 'null',
          description: 'The currently displayed month (use with .sync modifier)'
        },
        {
          name: 'maxWidth',
          type: 'String | Number',
          default: '350',
          description: 'Maximum width of the date picker. Accepts number (converted to px) or string with units'
        },
        {
          name: 'range',
          type: 'Boolean',
          default: 'false',
          description: 'Enable date range selection mode'
        },
        {
          name: 'min',
          type: 'String',
          default: 'null',
          description: 'Minimum allowed date (ISO 8601 format: YYYY-MM-DD)'
        },
        {
          name: 'max',
          type: 'String',
          default: 'null',
          description: 'Maximum allowed date (ISO 8601 format: YYYY-MM-DD)'
        },
        {
          name: 'allowedDates',
          type: 'Function',
          default: 'null',
          description: 'Function to determine which dates are allowed. Receives date string, returns boolean'
        },
        {
          name: 'readonly',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the date picker read-only'
        },
        {
          name: 'showAdjacentMonths',
          type: 'Boolean',
          default: 'false',
          description: 'Show dates from adjacent months'
        },
        {
          name: 'firstDayOfWeek',
          type: 'Number',
          default: '0',
          description: 'Sets the first day of the week (0 = Sunday, 1 = Monday, etc.)'
        },
        {
          name: 'noTitle',
          type: 'Boolean',
          default: 'false',
          description: 'Hide the title section (year/date)'
        },
        {
          name: 'scrollable',
          type: 'Boolean',
          default: 'false',
          description: 'Make the date picker content scrollable'
        },
        {
          name: 'fullWidth',
          type: 'Boolean',
          default: 'false',
          description: 'Force the date picker to full width'
        }
      ]
    },

    datePickerEvents() {
      return [
        {
          name: '@input',
          parameters: 'value',
          description: 'Emitted when the date value changes (v-model)'
        },
        {
          name: '@change',
          parameters: 'value',
          description: 'Emitted when a date is selected'
        },
        {
          name: '@update:pickerDate',
          parameters: 'value',
          description: 'Emitted when the displayed month changes (use with .sync)'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      // Parse ISO date string (YYYY-MM-DD) and format as DD/MM/YYYY
      return DateTime.fromISO(dateString).toFormat('dd/MM/yyyy')
    },
    allowedWeekdays(val) {
      const day = new Date(val).getDay()
      return day !== 0 && day !== 6 // Exclude Sunday (0) and Saturday (6)
    }
  }
}
</script>

<style scoped>
.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>
